import { Icon, Tooltip } from 'antd';
import { MessageDataTypes, MessagesData } from 'interfaces/messagesData';
import React from 'react';
import { FormattedMessage } from 'react-intl';

interface Props {
  selectedMonth: number | null;
  selectedYear: number | null;
  messagesData: MessagesData;
  type: MessageDataTypes;
  letfBorder?: boolean;
}

export function MessageDataNumbers({ selectedMonth, selectedYear, messagesData, type, letfBorder = false }: Props) {
  const getMessageTypeNumberByMonth = (year: number, month: number, type: MessageDataTypes) => {
    const monthData = messagesData[year][month];

    if (type === 'crm') {
      return monthData.empresa + monthData.lead + monthData.negocio + monthData.contato;
    }

    return monthData[type];
  };

  const getMessageTypeNumberTotal = () => {
    let typeSum = 0;
    for (const year of Object.keys(messagesData)) {
      for (const month of Object.keys(messagesData[year])) {
        typeSum += getMessageTypeNumberByMonth(Number(year), Number(month), type);
      }
    }

    return typeSum;
  };

  const getMessageTypeNumber = () => {
    if (!messagesData) {
      return 0;
    }

    if (selectedYear && selectedMonth) {
      return getMessageTypeNumberByMonth(selectedYear, selectedMonth, type);
    }

    return getMessageTypeNumberTotal();
  };

  const getMessageTypePercentageByMonth = (year: number, month: number) => {
    const typeNumber = getMessageTypeNumberByMonth(year, month, type);

    if (type === 'sent' || type === 'received') {
      const totalNumber = getMessageTypeNumberByMonth(year, month, 'total');
      const percentage = (typeNumber / totalNumber) * 100;
      return Math.round(percentage * 100) / 100;
    }

    const totalNumber = getMessageTypeNumberByMonth(year, month, 'sent');
    if (totalNumber === 0) return 0;

    const percentage = (typeNumber / totalNumber) * 100;
    return Math.round(percentage * 100) / 100;
  };

  const getMessageTypePercentageTotal = () => {
    let typeSum = 0;
    let totalSum = 0;
    for (const year of Object.keys(messagesData)) {
      for (const month of Object.keys(messagesData[year])) {
        typeSum += getMessageTypeNumberByMonth(Number(year), Number(month), type);

        totalSum +=
          type === 'sent' || type === 'received'
            ? getMessageTypeNumberByMonth(Number(year), Number(month), 'total')
            : getMessageTypeNumberByMonth(Number(year), Number(month), 'sent');
      }
    }

    if (totalSum === 0) return 0;

    const percentage = (typeSum / totalSum) * 100;
    return Math.round(percentage * 100) / 100;
  };

  const getMessageTypePercentage = () => {
    if (!messagesData) {
      return 0;
    }

    if (selectedYear && selectedMonth) {
      return getMessageTypePercentageByMonth(selectedYear, selectedMonth);
    }

    return getMessageTypePercentageTotal();
  };

  const getMessageTypeEvolution = () => {
    if (!selectedYear || !selectedMonth || !messagesData) {
      return 0;
    }

    const currentMonthData = getMessageTypeNumberByMonth(selectedYear, selectedMonth, type);

    let previousMonthYear = selectedYear;
    let previousMonth = selectedMonth - 1;

    if (previousMonth === 0) {
      previousMonth = 12;
      previousMonthYear = selectedYear - 1;
    }

    let previousMonthData = getMessageTypeNumberByMonth(previousMonthYear, previousMonth, type);

    const currentDate = new Date();

    if (currentDate.getMonth() + 1 === selectedMonth) {
      previousMonthData = getPreviousMonthPartialData(previousMonthData);
    }

    const difference = currentMonthData - previousMonthData;

    if (type === 'sent') {
      console.log({ currentMonthData, previousMonthData });
    }

    if (difference === 0) {
      return 0;
    }

    const comparrison = previousMonthData !== 0 ? previousMonthData : 1;
    let percentage = (difference / comparrison) * 100;

    if (type === 'sent') {
      console.log({ percentage, difference, comparrison });
    }

    percentage = Math.round(percentage * 100) / 100;

    return percentage;
  };

  const renderMessageTypeEvolution = () => {
    const evolutionPercentage = getMessageTypeEvolution();

    if (evolutionPercentage === 0) {
      return <div className={'entityPercentageEvolution'}>-</div>;
    }

    const iconType = evolutionPercentage > 0 ? 'arrow-up' : 'arrow-down';
    const evolutionClass = evolutionPercentage > 0 ? 'positiveEvolution' : 'negativeEvolution';

    return (
      <div className={`entityPercentageEvolution ${evolutionClass}`}>
        {/* 
        // @ts-ignore */}
        <Icon type={iconType} />
        {evolutionPercentage}%
      </div>
    );
  };

  const getPreviousMonthPartialData = (previousMonthData: number) => {
    const currentDate = new Date();
    const totalDaysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();

    const daysPassed = currentDate.getDate();

    const monthPercentage = daysPassed / totalDaysInMonth;

    if (type === 'sent') {
      console.log({
        previousMonthData,
        monthPercentage,
        daysPassed,
        totalDaysInMonth,
      });
    }

    return previousMonthData * monthPercentage;
  };

  const isPreviousMonthDataAvailable = () => {
    if (!selectedYear || !selectedMonth || !messagesData) {
      return false;
    }

    let previousMonthYear = selectedYear;
    let previousMonth = selectedMonth - 1;

    if (previousMonth === 0) {
      previousMonth = 12;
      previousMonthYear = selectedYear - 1;
    }

    if (!(previousMonthYear in messagesData)) {
      return false;
    }

    if (!(previousMonth in messagesData[previousMonthYear])) {
      return false;
    }

    return true;
  };

  function formatNumberWithDots(number: number) {
    const numberString = String(number);
    return numberString.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  return (
    <>
      {letfBorder && <div className="borderLeft" />}
      <div className="entityNumbersTitle">
        <FormattedMessage id={type} />
      </div>

      <div className="entityMessages">{formatNumberWithDots(getMessageTypeNumber())}</div>

      <Tooltip
        placement="bottomLeft"
        title={
          type === 'sent' || type === 'received' ? (
            <FormattedMessage id="percentage_total_tooltip" />
          ) : (
            <FormattedMessage id="percentage_sent_tooltip" />
          )
        }
      >
        <div className="entityPercentage">{getMessageTypePercentage()}%</div>
      </Tooltip>
      {isPreviousMonthDataAvailable() && (
        <Tooltip placement="bottomLeft" title={<FormattedMessage id="percentage_evolution_tooltip" />}>
          {renderMessageTypeEvolution()}
        </Tooltip>
      )}
    </>
  );
}
